/*
Copyright (c) 2015, salesforce.com, inc. All rights reserved.
Copyright (c) 2017, Picsa Advisory Services (Pty) Ltd. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES LOSS OF USE, DATA, OR PROFITS OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

import React from 'react'
import PropTypes from 'prop-types'

import { LinkButton } from '../'

export default class TabbedView extends React.PureComponent {
  static propTypes = {
    tabs: PropTypes.arrayOf(PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.element
    ])).isRequired,
    contents: PropTypes.arrayOf(PropTypes.element).isRequired,
    selectedTab: PropTypes.number,
    onClick: PropTypes.func,
    tabsClassName: PropTypes.string,
    backgroundColour: PropTypes.string,
    loadContentOnTabClick: PropTypes.bool,
    disabledTabs: PropTypes.arrayOf(PropTypes.number),
    alwaysFireOnClick: PropTypes.bool
  }

  static defaultProps = {
    selectedTab: 0,
    tabsClassName: '',
    loadContentOnTabClick: false,
    alwaysFireOnClick: false
  }

  constructor(props) {
    super(props)

    this.activeTabRef = React.createRef()
  }

  focusOnActiveTab = () => {
    window.scrollTo(0, this.activeTabRef.current.scrollIntoView())
  }

  handleTabClick = (tabIndex) => {
    if (this.props.selectedTab !== tabIndex || this.props.alwaysFireOnClick) {
      if (this.props.onClick) {
        this.props.onClick(tabIndex)
      }
    }
  }

  getTabs = () => (
    this.props.tabs.map((tab, index) => (
      <Tab
        title={tab}
        activeTab={this.props.selectedTab === index}
        index={index}
        disabled={this.props.disabledTabs && this.props.disabledTabs.includes(index)}
        onClick={this.handleTabClick}
        key={`tab_${index}`}
      />
    ))
  )

  getTabAssociatedContent = (backgroundColour) => (
    this.props.contents.map((content, index) => (
      <Content
        content={content}
        index={index}
        activeTab={this.props.selectedTab}
        backgroundColour={backgroundColour}
        key={`content_${index}`}
      />
    ))
  )

  render() {
    let tabs = this.getTabs()
    let contents = this.getTabAssociatedContent(this.props.backgroundColour)

    return (
      <React.Fragment>
        <div
          ref={this.activeTabRef}
          className={`slds-tabs_default ${this.props.tabsClassName}`}
          style={this.props.backgroundColour ? { backgroundColor: this.props.backgroundColour } : null}>
          <ul className='slds-tabs_default__nav' role='tablist' style={{ margin: 'auto' }}>
            {tabs}
          </ul>
        </div>
        {this.props.loadContentOnTabClick || this.props.disabledTabs
          ? this.props.disabledTabs && this.props.disabledTabs.indexOf(this.props.selectedTab)
            ? contents[this.props.selectedTab]
            : contents[this.props.selectedTab]
          : contents
        }
      </React.Fragment>
    )
  }
}

const Tab = ({ activeTab, index, title, disabled, onClick }) => (
  <li
    className={`slds-tabs--default__item tabbed-view_tab_title ${disabled ? 'tabbed-view_tab-disabled' : `${activeTab ? 'active focus' : ''}`}`}
    title={title}
    role='presentation'>
    <LinkButton
      id={`tab-default-${index}__item`}
      className={`slds-tabs_default__link ${disabled ? 'tabbed-view_tab-disabled' : ''}`}
      onClick={() => disabled ? null : onClick(index)}>
      {title}
    </LinkButton>
  </li>
)

Tab.propTypes = {
  title: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.element
  ]).isRequired,
  activeTab: PropTypes.bool,
  onClick: PropTypes.func.isRequired
}

const Content = ({ activeTab, index, content, backgroundColour }) => (
  <div
    id={`tab-default-${index}`}
    role="tabpanel"
    aria-labelledby={`tab-default-${index}__item`}
    className={`slds-p-top_x-small ${activeTab === index ? "slds-show" : "slds-hide"}`}
    style={backgroundColour ? { backgroundColor: backgroundColour } : null}>
    {content}
  </div>
)

Content.propTypes = {
  activeTab: PropTypes.number,
  content: PropTypes.element
}



// WEBPACK FOOTER //
// ./src/components/tabbed-view/index.js